import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t } = useTranslation();

  const services = [
    "Commercial Cleaning",
    "Medical / Clinic Cleaning",
    "Office Cleaning",
    "Car Dealership Cleaning",
    "Move In / Move Out",
    "Airbnb Cleaning",
  ];

  return (
    <footer style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Upper Footer */}
      <div className="bg-[#0F2A44] pt-16 pb-10 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div
              className="inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-[#D4AF37] mb-5 cursor-pointer transition-all duration-300 hover:bg-[#B8952E] hover:scale-105"
              style={{ boxShadow: "0 0 0 0 rgba(212,175,55,0.5)" }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  "0 0 18px 6px rgba(212,175,55,0.45)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  "0 0 0 0 rgba(212,175,55,0.5)";
              }}
            >
              <img
                src="https://storage.readdy-site.link/project_files/a749ed34-0552-42ad-80d0-bc014c702bfa/ddb227b9-44ac-4d46-8046-3ebd50701fa1_49D95226-89DE-44DF-B084-CFB8EB8D11D6.png?v=a9bb350f072ed3787796e06870b2d213"
                alt="Prime Shine WA"
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              {t("footer_desc")}
            </p>
            <div className="flex gap-3">
              {[
                { icon: "ri-facebook-fill", href: "#" },
                { icon: "ri-instagram-fill", href: "#" },
                { icon: "ri-twitter-x-fill", href: "#" },
                { icon: "ri-linkedin-fill", href: "#" },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-white/20 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all duration-200 cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-5">
              <a href="#quicklinks">{t("footer_quick_links")}</a>
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: t("nav_home"), to: "/" },
                { label: t("nav_services"), to: "/services" },
                { label: t("nav_about"), to: "/about" },
                { label: t("nav_pricing"), to: "/pricing" },
                { label: t("nav_reviews"), to: "/reviews" },
                { label: t("nav_faq"), to: "/faq" },
                { label: t("nav_contact"), to: "/contact" },
              ].map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-5">
              <a href="#services-list">{t("footer_services")}</a>
            </h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s}>
                  <Link
                    to="/services"
                    className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors duration-200"
                  >
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-5">
              <a href="#contact">{t("footer_contact")}</a>
            </h4>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-map-pin-2-line text-[#D4AF37] text-base" />
                </div>
                <span className="text-white/60 text-sm">Seattle, WA &amp; Greater Area</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-phone-line text-[#D4AF37] text-base" />
                </div>
                <a href="tel:+12064744668" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors duration-200">
                  +1 (206) 474-4668
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-mail-line text-[#D4AF37] text-base" />
                </div>
                <a href="mailto:info@primeshinewa.com" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors duration-200">
                  info@primeshinewa.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-time-line text-[#D4AF37] text-base" />
                </div>
                <span className="text-white/60 text-sm">Mon–Fri: 8am–6pm | Sat: 9am–4pm</span>
              </li>
            </ul>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2">
              {["Insured", "Bonded", "5-Star"].map((b) => (
                <span
                  key={b}
                  className="text-xs text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-3 py-1"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lower Footer */}
      <div className="bg-[#091A2B] px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-white/40 text-xs">{t("footer_copyright")}</p>
          <div className="flex items-center gap-4">
            <a href="#" rel="nofollow" className="text-white/40 hover:text-[#D4AF37] text-xs transition-colors duration-200">
              {t("footer_privacy")}
            </a>
            <span className="text-white/20">|</span>
            <a href="#" rel="nofollow" className="text-white/40 hover:text-[#D4AF37] text-xs transition-colors duration-200">
              {t("footer_terms")}
            </a>
            <span className="text-white/20">|</span>
            <a
              href="https://www.vanqode.com"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-white/40 hover:text-[#D4AF37] text-xs transition-colors duration-200 whitespace-nowrap"
            >
              Powered by Vanqode
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
